import { FC, memo, useEffect, useRef, useState } from "react";

import { PercentageDisplayProps } from "../utils/types";

const PercentageDisplay: FC<PercentageDisplayProps> = ({
  score = 0,
  viewType,
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const animationRef = useRef<number | null>(null);
  const size = viewType === "msite" ? 40 : 56;
  const strokeWidth = viewType === "msite" ? 4 : 6;
  const radius = (size - strokeWidth) / 2;
  const progress = Math.min(100, Math.max(0, score));

  useEffect(() => {
    let startTimestamp: number | null = null;
    const animationDuration = 500;
    const animate = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutCubic(progress);
      setAnimatedPercentage(
        (prevPercentage) =>
          prevPercentage + (score - prevPercentage) * easedProgress,
      );
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [score]);
  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
  const describeArc = (
    startAngle: number,
    angleExtent: number,
    isComplete = false,
  ) => {
    if (isComplete) {
      return `
        M ${size / 2} ${strokeWidth / 2}
        A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}
        A ${radius} ${radius} 0 0 1 ${size / 2} ${size - strokeWidth / 2}
        A ${radius} ${radius} 0 0 1 ${strokeWidth / 2} ${size / 2}
        A ${radius} ${radius} 0 0 1 ${size / 2} ${strokeWidth / 2}
      `;
    }
    const start = {
      x: size / 2 + radius * Math.cos((startAngle * Math.PI) / 180),
      y: size / 2 + radius * Math.sin((startAngle * Math.PI) / 180),
    };
    const end = {
      x:
        size / 2 +
        radius * Math.cos(((startAngle + angleExtent) * Math.PI) / 180),
      y:
        size / 2 +
        radius * Math.sin(((startAngle + angleExtent) * Math.PI) / 180),
    };
    const largeArc = angleExtent > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  const getColors = (score: number) => {
    if (score < 60) return { stroke: "#E03923", text: "#dc2626" };
    if (score <= 80) return { stroke: "#FFA000", text: "#ea580c" };
    return { stroke: "#228568", text: "#16a34a" };
  };

  const colors = getColors(animatedPercentage);
  const startGap = 15;
  const endGap = 15;
  const totalGap = startGap + endGap;
  const availableAngle = 360 - totalGap;
  const progressAngle = (animatedPercentage / 100) * availableAngle;
  const progressStart = startGap;
  const remainingStart = progressStart + progressAngle + endGap;
  const remainingAngle = availableAngle - progressAngle;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size}>
        {progress < 100 ? (
          <>
            <path
              d={describeArc(remainingStart, remainingAngle)}
              className="stroke-gray-200"
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
            />
            <path
              d={describeArc(progressStart, progressAngle)}
              stroke={colors.stroke}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
            />
          </>
        ) : (
          <path
            d={describeArc(0, 360, true)}
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            fill="none"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={`${["modal", "web"].includes(viewType) ? "text-content-primary text-base font-bold" : "text-[10px] font-normal text-white"}`}
        >
          {Math.round(animatedPercentage)}%
        </span>
      </div>
    </div>
  );
};

export default memo(PercentageDisplay);
